import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Modal from '@material-ui/core/Modal';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import {
  RadioGroup,
  Radio,
  TableBody,
  ListItem,
  Select,
  FormControl,
  Input,
  InputLabel,
  ListItemText
} from '@material-ui/core';
import RequiredButton from '../../components/atoms/RequiredButton';
import AnyButton from '../../components/atoms/AnyButton';
import CancelButton from '../../components/atoms/CancelButton';
import ConfirmButton from '../../components/atoms/ConfirmButton';
import FileUpload from '../../components/molecules/FileUpload';
import GochiMenuEditConfirm from './GochiMenuEditConfirm';
import {
  getGochiMenus,
  editGochiMenu,
  addGochiMenuImage
} from '../../actions/GochiMenus';
import { resizeImage, uploadFile, sendLogin } from '../../auth/Auth';
import ValidationText from '../../components/atoms/ValidationText';
import listMenuChildTypes from '../../constants/MenuChildType';
import { languages, languageType } from '../../constants/Language';
import enabledStripeBilling from '../../constants/StripeBilling';
import usageTypes from '../../constants/UsageType';

const styles = theme => ({
  root: {
    position: 'absolute',
    width: '85%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '95%',
      padding: 16
    }
  },
  pageTitle: {
    margin: '20px 0px 0px',
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      marginBottom: 10
    }
  },
  mainPaper: {
    position: 'relative',
    margin: '1px 0px 10px',
    padding: '10px 26px 10px 26px',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 5px'
    },
    display: 'flex',
    flexDirection: 'column'
  },
  alignEnd: {
    alignSelf: 'end'
  },
  editTable: {
    width: 'auto',
    [theme.breakpoints.down('sm')]: {
      display: 'block'
    },
    '& tr': {
      [theme.breakpoints.down('sm')]: {
        display: 'block'
      }
    }
  },
  tableCellBorderNone: {
    borderBottom: 'none'
  },
  textField100: {
    padding: '0px 5px 0px 5px',
    width: '100px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  textFieldLong: {
    padding: '0px 5px 0px 5px',
    width: '500px',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  priceCalc: {
    margin: '0px 5px',
    color: '#aaa'
  },
  colLeft: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      border: 0,
      paddingBottom: 5
    }
  },
  colRight: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      border: 0,
      paddingTop: 0
    }
  },
  hideMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  width100: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      display: 'block',
      padding: 0
    }
  },
  displayFlex: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      border: 0,
      paddingTop: 0
    }
  },
  displayBlock: {
    display: 'block'
  },
  textMutiple: {
    paddingTop: '46px',
    width: '33px'
  },
  textFieldPriceCombo: {
    padding: '0px 5px 0px 5px',
    width: '200px',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  situationPulldown: {
    width: '100%',
    margin: '16px 0px 8px'
  },
  pulldownHeight: {
    maxHeight: '350px'
  }
});

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      name: state.loginInfo.name,
      detail: state.gochiMenus.detail,
      shops: state.shops.shops,
      taxTypes: state.gochiMenus.taxTypes,
      reducedTaxMenuPrefix: state.commons.reducedTaxMenuPrefix
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          getGochiMenus,
          editGochiMenu,
          addGochiMenuImage
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openEditProgress', 'updateOpenEditProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isEditSuccess', 'updateIsEditSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('menuChildType', 'setMenuChildType', 1),
  withState('menuCombo', 'setMenuCombo', 0),
  withState('groupId', 'setGroupId', 0),
  withState('groupName', 'setGroupName', ''),
  withState('shopId', 'setShopId', 0),
  withState('shopName', 'setShopName', ''),
  withState('menuId', 'setMenuId', 0),
  withState('menuName', 'setMenuName', ''),
  withState('menuNameError', 'setMenuNameError', ''),
  withState('price', 'setPrice', ''),
  withState('priceError', 'setPriceError', ''),
  withState('priceCalc', 'setPriceCalc', '000 (税込み)'),
  withState('priceCombo', 'setPriceCombo', ''),
  withState('priceComboError', 'setPriceComboError', ''),
  withState('quantity', 'setQuantity', ''),
  withState('quantityReleaseError', 'setQuantityReleaseError', ''),
  withState('subscripIntervalCountError', 'setSubscripIntervalCountError', ''),
  withState('quantityFree', 'setQuantityFree', ''),
  withState('quantityPromotionError', 'setQuantityPromotionError', ''),
  withState('quantityFreeDes', 'setQuantityFreeDes', ''),
  withState('numberUsePerDay', 'setNumberUsePerDay', 0),
  withState('expirationType', 'setExpirationType', 0),
  withState('expiration', 'setExpiration', ''),
  withState('expirationError', 'setExpirationError', ''),
  withState('expirationEndDate', 'setExpirationEndDate', ''),
  withState('minExpirationDate', 'setMinExpirationDate', () => {
    moment.tz.setDefault('Asia/Tokyo');
    return moment().format('YYYY-MM-DD');
  }),
  withState('maxExpirationDate', 'setMaxExpirationDate', () => {
    moment.tz.setDefault('Asia/Tokyo');
    return moment()
      .add(180, 'days')
      .format('YYYY-MM-DD');
  }),
  withState('expirationList', 'setExpirationList', () => {
    const tmpList = [];
    for (let i = 1; i <= 180; i += 1) {
      tmpList.push({
        label: `${i}日間`,
        value: String(i)
      });
    }
    return tmpList;
  }),
  withState('quantityReleaseList', 'setQuantityReleaseList', () => {
    const tmpList = [];
    for (let i = 0; i <= 100; i += 1) {
      tmpList.push({
        label: `${i}`,
        value: String(i)
      });
    }
    return tmpList;
  }),
  withState('description', 'setDescription', ''),
  withState('taxType', 'setTaxType', 'INTERNAL'),
  withState('reducedTaxCheck', 'setReducedTaxCheck', false),
  withState('reducedTaxType', 'setReducedTaxType', 'UNREDUCED'),
  withState('tagStr', 'setTagStr', ''),
  withState('tags', 'setTags', null),
  withState('image', 'setImage', null),
  withState('previewImage', 'setPreviewImage', null),
  withState('updatedAt', 'setUpdatedAt', ''),
  withState('isLanguage', 'setIsLanguage', 'ja'),
  withState('translateType', 'setTranslateType', 0),
  withState('multiLangData', 'setMultiLangData', {}),
  withState('menuGenreId', 'setMenuGenreId', null),
  withState('menuGenreName', 'setMenuGenreName', ''),
  withState('subscriptionInterval', 'setSubscriptionInterval', 'WEEK'),
  withState('subscriptionIntervalCount', 'setSubscriptionIntervalCount', ''),
  withState('subscription', 'setSubscription', 0),
  withState('menuSituationIds', 'setmenuSituationIds', []),
  withState('menuSituationNames', 'setMenuSituationNames', ''),
  withState('menuUserTargetIds', 'setmenuUserTargetIds', []),
  withState('menuUserTargetNames', 'setMenuUserTargetNames', ''),
  withState('menuLimitTotal', 'setMenuLimitTotal', 0),
  withState('menuGenreError', 'setMenuGenreError', ''),
  withState('showPrice', 'setShowPrice', 1),
  withState('usageType', 'setUsageType', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    defaultPrice: props => () => {
      let includedTaxPrice = 0;
      let excludedTaxPrice = 0;
      // TODO DBから取得
      let tax = 0.1;
      if (props.editInput.reducedTaxCheck) {
        tax = 0.08;
      }

      if (props.editInput.price !== '') {
        if (props.editInput.taxType === 'INTERNAL') {
          const amountTemp = parseFloat(
            (props.editInput.price / (1 + tax)).toFixed(9)
          );
          const taxAmount = Math.round(amountTemp * tax);
          includedTaxPrice = props.editInput.price;
          excludedTaxPrice = props.editInput.price - taxAmount;
        } else if (props.editInput.taxType === 'DONATION') {
          includedTaxPrice = props.editInput.price;
          excludedTaxPrice = props.editInput.price;
        } else {
          // EXTERNAL
          includedTaxPrice =
            props.editInput.price + Math.round(props.editInput.price * tax);
          excludedTaxPrice = props.editInput.price;
        }
      }
      console.log(excludedTaxPrice);
      // props.setPriceCalc(`${includedTaxPrice} (税抜 ${excludedTaxPrice})`);
      const priceShow = includedTaxPrice
        ? parseInt(includedTaxPrice, 0).toString()
        : '0';
      props.setPriceCalc(`${priceShow} (税込み)`);
    }
  }),
  lifecycle({
    componentDidMount() {
      this.props.setMenuChildType(this.props.editInput.menuChildType);
      this.props.setGroupId(this.props.editInput.groupId);
      this.props.setGroupName(this.props.editInput.groupName);
      this.props.setShopId(this.props.editInput.shopId);
      this.props.setShopName(this.props.editInput.shopName);
      this.props.setMenuId(this.props.editInput.menuId);
      this.props.setMenuName(this.props.editInput.menuName);
      this.props.setPrice(this.props.editInput.price);
      this.props.setMenuCombo(this.props.editInput.menuCombo);
      this.props.setUsageType(this.props.editInput.usageType);
      this.props.setMenuLimitTotal(this.props.editInput.menuLimitTotal || 0);
      this.props.setSubscription(this.props.editInput.subscription);
      this.props.setShowPrice(this.props.editInput.showPrice);
      this.props.setSubscriptionIntervalCount(
        this.props.editInput.subscriptionIntervalCount
      );
      this.props.setSubscriptionInterval(
        this.props.editInput.subscriptionInterval
      );
      this.props.setPriceCombo(this.props.editInput.priceCombo);
      this.props.setQuantity(this.props.editInput.quantity);
      this.props.setQuantityFree(this.props.editInput.quantityFree);
      this.props.setQuantityFreeDes(this.props.editInput.quantityFreeDes);
      this.props.setNumberUsePerDay(this.props.editInput.numberUsePerDay);
      this.props.setExpiration(this.props.editInput.expiration);
      if (this.props.editInput.expirationEndDate) {
        this.props.setExpirationType(1);
      } else if (this.props.editInput.expiration) {
        this.props.setExpirationType(0);
      } else {
        this.props.setExpirationType(2);
      }
      this.props.setExpirationEndDate(this.props.editInput.expirationEndDate);
      this.props.setDescription(this.props.editInput.description);
      this.props.setTaxType(this.props.editInput.taxType);
      this.props.setReducedTaxType(this.props.editInput.reducedTaxType);
      if (this.props.editInput.reducedTaxType === 'REDUCED') {
        this.props.setReducedTaxCheck(true);
      }
      this.props.setTags(this.props.editInput.tags);
      this.props.setTagStr(this.props.editInput.tagStr);
      this.props.setPreviewImage(this.props.editInput.image);
      this.props.setUpdatedAt(this.props.editInput.updatedAt);
      this.props.defaultPrice();
      this.props.setMinExpirationDate(
        this.props.editInput.createdAt.split('T')[0]
      );
      this.props.setMaxExpirationDate(
        moment(this.props.editInput.createdAt.split('T')[0])
          .add(180, 'days')
          .format('YYYY-MM-DD')
      );
      if (this.props.editInput.menuGenreId) {
        this.props.setMenuGenreId(this.props.editInput.menuGenreId.toString());
      } else {
        this.props.setMenuGenreId(null);
      }

      if (
        this.props.editInput.menuSituationIds &&
        this.props.editInput.menuSituationIds.length
      ) {
        this.props.setmenuSituationIds(
          this.props.editInput.menuSituationIds.map(String)
        );
      } else {
        this.props.setmenuSituationIds([]);
      }

      if (
        this.props.editInput.menuUserTargetIds &&
        this.props.editInput.menuUserTargetIds.length
      ) {
        this.props.setmenuUserTargetIds(
          this.props.editInput.menuUserTargetIds.map(String)
        );
      } else {
        this.props.setmenuUserTargetIds([]);
      }

      let dataFirst = {};
      const origilData = {
        shopIdAdd: null,
        menuChildType: null,
        menuCombo: null,
        menuLimitTotal: null,
        subscription: null,
        subscriptionIntervalCount: null,
        subscriptionInterval: null,
        groupId: null,
        groups: null,
        shops: null,
        shopName: null,
        groupName: null,
        menuName: null,
        price: null,
        priceCalc: null,
        priceCombo: null,
        quantity: null,
        quantityFree: null,
        quantityFreeDes: null,
        numberUsePerDay: null,
        expirationType: null,
        expiration: null,
        expirationEndDate: null,
        minExpirationDate: null,
        maxExpirationDate: null,
        expirationList: null,
        quantityReleaseList: null,
        description: null,
        taxType: null,
        reducedTaxCheck: null,
        reducedTaxType: null,
        tagStr: null,
        tags: null,
        updatedAt: null,
        menuGenreId: null,
        menuGenreName: null,
        menuSituationIds: [],
        menuSituationNames: null,
        menuUserTargetIds: [],
        menuUserTargetNames: null,
        showPrice: null,
        usageType: null
      };
      dataFirst = origilData;
      if (this.props.editInput.otherLanguages) {
        const str = JSON.stringify(this.props.editInput.otherLanguages);
        dataFirst.otherLanguages = JSON.parse(str);
      }
      languages.forEach(item => {
        if (item.value === languageType.ORIGIN) return;
        const translatedData = dataFirst.otherLanguages.find(
          el => el.language === item.value
        );
        if (!translatedData) {
          const other = {
            translateType: 0,
            language: item.value
          };
          dataFirst.otherLanguages.push(other);
        }
      });
      this.props.setMultiLangData(dataFirst);
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    searchKey,
    roles,
    editInput,
    openEditConfirm,
    updateOpenEdit,
    updateOpenEditConfirm,
    openEditProgress,
    updateOpenEditProgress,
    loading,
    updateLoading,
    isEditSuccess,
    updateIsEditSuccess,
    resultMessage,
    setResultMessage,
    menuChildType,
    groupId,
    groupName,
    shopId,
    shopName,
    menuId,
    menuName,
    setMenuName,
    menuCombo,
    menuLimitTotal,
    price,
    setPrice,
    priceCalc,
    setPriceCalc,
    priceCombo,
    setPriceCombo,
    priceComboError,
    setPriceComboError,
    subscriptionInterval,
    setSubscriptionInterval,
    subscriptionIntervalCount,
    setSubscriptionIntervalCount,
    subscripIntervalCountError,
    setSubscripIntervalCountError,
    subscription,
    setSubscription,
    quantity,
    quantityReleaseList,
    setQuantity,
    quantityReleaseError,
    setQuantityReleaseError,
    quantityFree,
    setQuantityFree,
    quantityPromotionError,
    setQuantityPromotionError,
    numberUsePerDay,
    setNumberUsePerDay,
    description,
    setDescription,
    quantityFreeDes,
    setQuantityFreeDes,
    expirationType,
    setExpirationType,
    expiration,
    setExpiration,
    expirationError,
    setExpirationError,
    expirationEndDate,
    setExpirationEndDate,
    minExpirationDate,
    maxExpirationDate,
    expirationList,
    taxType,
    setTaxType,
    taxTypes,
    reducedTaxCheck,
    setReducedTaxCheck,
    reducedTaxType,
    setReducedTaxType,
    tagStr,
    setTagStr,
    tags,
    setTags,
    image,
    setImage,
    previewImage,
    setPreviewImage,
    updatedAt,
    reducedTaxMenuPrefix,
    menuNameError,
    setMenuNameError,
    priceError,
    setPriceError,
    isLanguage,
    setIsLanguage,
    multiLangData,
    setMultiLangData,
    translateType,
    setTranslateType,
    menuGenreId,
    setMenuGenreId,
    menuGenreName,
    setMenuGenreName,
    menuSituationIds,
    setmenuSituationIds,
    menuSituationNames,
    setMenuSituationNames,
    menuUserTargetIds,
    setmenuUserTargetIds,
    menuUserTargetNames,
    setMenuUserTargetNames,
    menuGenre,
    menuSituation,
    menuUserTarget,
    menuGenreError,
    setMenuGenreError,
    showPrice,
    setShowPrice,
    usageType
  }) => {
    const errors = {
      menuNameError,
      priceError,
      priceComboError,
      quantityReleaseError,
      quantityPromotionError,
      subscripIntervalCountError,
      expirationError,
      menuGenreError
    };

    const validMenuName = () => {
      if (isLanguage !== languageType.ORIGIN && translateType === 0) {
        return 0;
      }
      const value = menuName;
      if (value.trim() === '') {
        setMenuNameError('必須項目です。');
        return 1;
      }
      setMenuNameError('');
      setMenuName(value);
      return 0;
    };
    const validMenuGenre = () => {
      const value = menuGenreId;
      if (!value) {
        setMenuGenreError('必須項目です。');
        return 1;
      }
      setMenuGenreError('');
      setMenuGenreId(value);
      return 0;
    };
    const validPrice = () => {
      const value = price;
      if (value === '') {
        setPriceError('必須項目です。');
        return 1;
      }
      if (!/^\d+$/.test(value)) {
        setPriceError('半角数字のみ入力してください。');
        return 1;
      }
      setPriceError('');
      setPrice(value);
      return 0;
    };
    const validPriceCombo = () => {
      const value = priceCombo;
      if (value === '') {
        setPriceComboError('必須項目です。');
        return 1;
      }
      if (!/^\d+$/.test(value)) {
        setPriceComboError('半角数字のみ入力してください。');
        return 1;
      }
      setPriceComboError('');
      setPriceCombo(parseInt(value, 10));
      return 0;
    };
    const validQuantityRelease = () => {
      const value = quantity;
      if (value === '') {
        setQuantityReleaseError('必須項目です。');
        return 1;
      }
      if (!/^[1-9]|[1-9]\d+$/.test(value)) {
        setQuantityReleaseError('半角数字のみ入力してください。');
        return 1;
      }
      setQuantityReleaseError('');
      setQuantity(parseInt(value, 10));
      return 0;
    };
    const validSubscriptionIntervalCount = () => {
      const checkSubscription = subscription;
      if (checkSubscription === 1) {
        const valueIntervalCount = subscriptionIntervalCount;
        const valueInterval = subscriptionInterval;
        if (valueIntervalCount === '') {
          setSubscripIntervalCountError('必須項目です。');
          return 1;
        }
        if (Number(valueIntervalCount) < 1) {
          setSubscripIntervalCountError('頻度価値は1以上ご入力ください');
          return 1;
        }
        if (!/^\d+$/.test(valueIntervalCount)) {
          setSubscripIntervalCountError('半角数字のみ入力してください。');
          return 1;
        }
        if (valueInterval === 'WEEK' && Number(valueIntervalCount) > 52) {
          setSubscripIntervalCountError(
            '頻度価値は週間サブスクに対して52以下、月間サブスクに対して12以下入力してください。'
          );
          return 1;
        }
        if (valueInterval === 'MONTH' && Number(valueIntervalCount) > 12) {
          setSubscripIntervalCountError(
            '頻度価値は週間サブスクに対して52以下、月間サブスクに対して12以下入力してください。'
          );
          return 1;
        }
      }
      setSubscripIntervalCountError('');
      return 0;
    };
    const validQuantityPromotion = () => {
      const value = quantityFree;
      if (value === '' || !value) {
        setQuantityPromotionError('');
        return 0;
      }
      if (!/^\d+$/.test(value)) {
        setQuantityPromotionError('半角数字のみ入力してください。');
        return 1;
      }
      setQuantityPromotionError('');
      setQuantityFree(parseInt(value, 10));
      return 0;
    };
    const validExpiration = () => {
      const value = expiration;
      if (value === '' || !value) {
        setExpirationError('');
        return 0;
      }
      if (!/\b([1-9]|[1-9][0-9]|1[0-7][0-9]|180)\b/.test(value)) {
        setExpirationError('半角数字のみ入力してください。From 1 - 180');
        return 1;
      }
      setExpirationError('');
      setExpiration(parseInt(value, 10));
      return 0;
    };

    const valid = () => {
      let errorCount = 0;
      // Menu normal
      if (menuCombo === 0) {
        errorCount =
          validMenuName() +
          validPrice() +
          validSubscriptionIntervalCount() +
          validMenuGenre() +
          validExpiration();
      } else if (menuCombo === 1) {
        // Menu combo
        errorCount =
          validMenuName() +
          // validPrice() +
          validPriceCombo() +
          validQuantityRelease() +
          validSubscriptionIntervalCount() +
          validQuantityPromotion() +
          validExpiration() +
          validMenuGenre();
      }

      if (errorCount === 0) {
        return true;
      }
      return false;
    };

    const resetExpirationValue = () => {
      setExpiration('');
      setExpirationEndDate('');
    };

    const getCurrentData = () => {
      const lang = isLanguage;
      let data = multiLangData;
      const origilParam = {
        menuChildType,
        groupId,
        shopId,
        shopName,
        groupName,
        menuName,
        price,
        priceCalc,
        description,
        subscription,
        menuCombo,
        menuLimitTotal:
          menuChildType !== 3 && menuCombo === 0 ? menuLimitTotal : undefined,
        subscriptionIntervalCount:
          subscription === 1 ? subscriptionIntervalCount : undefined,
        subscriptionInterval:
          subscription === 1 ? subscriptionInterval : undefined,
        priceCombo,
        quantity,
        quantityFree,
        quantityFreeDes,
        numberUsePerDay,
        expirationType,
        minExpirationDate,
        maxExpirationDate,
        // expirationList,
        // quantityReleaseList,
        expiration,
        expirationEndDate,
        taxType,
        reducedTaxType,
        tags,
        tagStr,
        updatedAt,
        menuGenreId,
        menuSituationIds,
        menuUserTargetIds,
        showPrice,
        usageType
      };
      const otherParam = {
        translateType,
        language: lang,
        params: {
          menuName,
          description,
          quantityFreeDes
        }
      };
      // origin
      if (lang === languageType.ORIGIN) {
        data = { ...data, ...origilParam };
      } else {
        // other languages
        for (let index = 0; index < data.otherLanguages.length; index += 1) {
          if (data.otherLanguages[index].language === lang) {
            data.otherLanguages[index] = otherParam;
          }
        }
      }
      const str = JSON.stringify(data);
      return JSON.parse(str);
    };

    const getGenreName = () => {
      if (menuGenreId) {
        const genreName = menuGenre.find(
          genre => genre.menuGenreId.toString() === menuGenreId.toString()
        ).menuGenreName;
        return genreName;
      }
      return '';
    };

    const getSituationName = () => {
      const situationName = [];
      if (menuSituationIds.length) {
        menuSituationIds.forEach(id => {
          const name = menuSituation.find(
            situation => situation.menuSituationId.toString() === id.toString()
          ).menuSituationName;
          situationName.push(name);
        });
        return situationName.join(', ');
      }
      return '';
    };

    const getUserTargetName = () => {
      const userTargetName = [];
      if (menuUserTargetIds.length) {
        menuUserTargetIds.forEach(id => {
          const names = menuUserTarget.find(
            userTarget =>
              userTarget.menuUserTargetId.toString() === id.toString()
          ).name;
          userTargetName.push(names);
        });
        return userTargetName.join(', ');
      }
      return '';
    };

    const cancelAction = () => updateOpenEdit(false);
    const confirmAction = () => {
      if (!valid()) {
        alert('登録情報を正しく入力してください。');
      } else {
        setMenuGenreName(getGenreName());
        setMenuSituationNames(getSituationName());
        setMenuUserTargetNames(getUserTargetName());
        setMultiLangData(getCurrentData());
        updateOpenEditConfirm(true);
      }
    };
    const backAction = () => updateOpenEditConfirm(false);
    const editAction = async () => {
      updateLoading(true);
      updateOpenEditProgress(true);
      const editedMenu = await actions
        .editGochiMenu(menuId, multiLangData)
        .then(data => checkSession(data));
      if (typeof image !== 'undefined' && image !== null) {
        let imageData = image;
        if (imageData.size > Number(process.env.REACT_APP_RESIZE_BORDER)) {
          const { resizeImageFile } = await resizeImage(imageData);
          imageData = resizeImageFile;
        }
        const imagePath = await uploadFile('menu', menuId, imageData, 'public');
        console.log('binhdp123', imagePath);
        actions
          .addGochiMenuImage(menuId, {
            image: imagePath
          })
          .then(data => checkSession(data));
      }
      if (editedMenu.type === 'EDIT_GOCHI_MENU_SUCCESS') {
        updateIsEditSuccess(true);
        setResultMessage('編集が完了しました。');
        updateLoading(false);
      } else {
        updateIsEditSuccess(false);
        setResultMessage('編集に失敗しました。');
        updateLoading(false);
      }
    };
    const progressOkAction = () => {
      if (isEditSuccess) {
        actions.getGochiMenus(searchKey).then(data => {
          checkSession(data);
          if (data.type === 'GET_GOCHI_MENUS_SUCCESS') {
            updateOpenEdit(false);
            updateOpenEditConfirm(false);
            updateOpenEditProgress(false);
          }
        });
      } else {
        updateOpenEditProgress(false);
      }
    };

    const calcPrice = e => {
      let includedTaxPrice = 0;
      let excludedTaxPrice = 0;

      let latestPrice = price;
      if (e.target.id === 'price') {
        latestPrice = e.target.value;
      }

      let latestTaxType = taxType;
      if (e.target.name === 'taxType') {
        latestTaxType = e.target.value;
      }

      let lastestPriceCombo = priceCombo;
      if (e.target.id === 'priceCombo') {
        lastestPriceCombo = e.target.value;
      }

      let lastestQuantityRelease = quantity;
      if (e.target.name === 'quantityRelease') {
        lastestQuantityRelease = e.target.value;
      }

      if (
        lastestPriceCombo &&
        lastestPriceCombo !== '' &&
        lastestQuantityRelease &&
        lastestQuantityRelease !== ''
      ) {
        setPrice(lastestPriceCombo * lastestQuantityRelease);
        latestPrice = lastestPriceCombo * lastestQuantityRelease;
      }
      if (
        (!lastestPriceCombo ||
          lastestPriceCombo === '' ||
          !lastestQuantityRelease ||
          lastestQuantityRelease === '') &&
        menuCombo === 1
      ) {
        setPrice(lastestPriceCombo * lastestQuantityRelease);
        setPriceCalc('000 (税込み)');
        latestPrice = lastestPriceCombo * lastestQuantityRelease;
      }

      // TODO DBから取得
      let tax = 0.1;
      if (
        (e.target.id !== 'reducedTax' && reducedTaxCheck) ||
        (e.target.id === 'reducedTax' && e.target.checked)
      ) {
        // TODO DBから取得
        tax = 0.08;
      }

      if (latestPrice !== '') {
        if (latestTaxType === 'INTERNAL') {
          const amountTemp = parseFloat((latestPrice / (1 + tax)).toFixed(9));
          const taxAmount = Math.round(amountTemp * tax);
          includedTaxPrice = latestPrice;
          excludedTaxPrice = latestPrice - taxAmount;
        } else if (latestTaxType === 'DONATION') {
          includedTaxPrice = latestPrice;
          excludedTaxPrice = latestPrice;
        } else {
          // EXTERNAL
          const taxAmount = Math.round(latestPrice * tax);
          includedTaxPrice = latestPrice * 1 + taxAmount;
          excludedTaxPrice = latestPrice;
        }
      }
      // setPriceCalc(`${includedTaxPrice} (税抜 ${excludedTaxPrice})`);
      console.log(excludedTaxPrice);
      const val =
        e.target.id !== 'reducedTax' && typeof e.target.id !== 'undefined'
          ? e.target.value
          : includedTaxPrice;
      console.log('val', val);
      const priceShow =
        val !== '' ? parseInt(includedTaxPrice, 0).toString() : '000';
      setPriceCalc(`${priceShow} (税込み)`);
    };
    const splitTagStr = str => {
      if (str === '') {
        setTags(null);
      } else {
        setTags(
          str.split(/\s+/).map(t => {
            return { tagName: t };
          })
        );
      }
    };
    const convertReducedTax = checked => {
      if (checked) {
        setReducedTaxType('REDUCED');
      } else {
        setReducedTaxType('UNREDUCED');
      }
    };

    // setting language
    const setLanguage = v => {
      const lang = isLanguage;
      const data = getCurrentData();
      if (
        (v !== languageType.ORIGIN && lang === languageType.ORIGIN) ||
        (v !== languageType.ORIGIN && lang !== languageType.ORIGIN)
      ) {
        // origin to other or other to other lang
        for (let k = 0; k < multiLangData.otherLanguages.length; k += 1) {
          if (multiLangData.otherLanguages[k].language === v) {
            setMenuName(
              multiLangData.otherLanguages[k].params &&
                multiLangData.otherLanguages[k].params.menuName
                ? multiLangData.otherLanguages[k].params.menuName
                : ''
            );
            setDescription(
              multiLangData.otherLanguages[k].params &&
                multiLangData.otherLanguages[k].params.description
                ? multiLangData.otherLanguages[k].params.description
                : ''
            );
            setTranslateType(
              multiLangData.otherLanguages[k].translateType
                ? multiLangData.otherLanguages[k].translateType
                : 0
            );
            setQuantityFreeDes(
              multiLangData.otherLanguages[k].params &&
                multiLangData.otherLanguages[k].params.quantityFreeDes
                ? multiLangData.otherLanguages[k].params.quantityFreeDes
                : ''
            );
          }
        }
      } else if (v === languageType.ORIGIN) {
        // other to origin
        setMenuName(multiLangData.menuName);
        setDescription(multiLangData.description);
        setQuantityFreeDes(multiLangData.quantityFreeDes);
      }
      setIsLanguage(v);
      setMultiLangData(data);
    };

    return (
      <div className={classes.root}>
        <div className={classes.pageTitle}>
          <Typography variant="h6">メニュー編集</Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <Select
            className={`${classes.textField} ${classes.alignEnd}`}
            value={isLanguage}
            onChange={e => {
              if (isLanguage === e.target.value) {
                return;
              }
              if (
                valid() ||
                (isLanguage !== languageType.ORIGIN && translateType === 0)
              ) {
                setLanguage(e.target.value);
              } else {
                alert('登録情報を正しく入力してください。');
              }
            }}
          >
            {languages.map(lang => (
              <MenuItem key={lang.value} value={lang.value}>
                {lang.label}
              </MenuItem>
            ))}
          </Select>
          {isLanguage !== languageType.ORIGIN && (
            <ListItem className={classes.itemForm}>
              <Typography component="div" className={classes.formInput}>
                <RadioGroup
                  value={translateType}
                  onChange={e => {
                    setTranslateType(parseInt(e.target.value, 10));
                  }}
                >
                  <FormControlLabel
                    value={0}
                    control={<Radio color="primary" />}
                    label="GG翻訳を選択"
                  />
                  <FormControlLabel
                    value={1}
                    control={<Radio color="primary" />}
                    label="自分で文章入力"
                  />
                </RadioGroup>
              </Typography>
            </ListItem>
          )}
          <Table className={classes.editTable}>
            <TableBody>
              {(roles.isSecretariat ||
                roles.isChildShop ||
                roles.isGroup ||
                roles.isChildGroup) &&
                isLanguage === languageType.ORIGIN && (
                  <>
                    <TableRow>
                      <TableCell className={classes.colLeft}>
                        <RequiredButton />
                      </TableCell>
                      <TableCell className={classes.colRight}>
                        <Typography className={classes.description}>
                          メニュー種別
                        </Typography>
                        <RadioGroup value={editInput.menuChildType}>
                          <FormControlLabel
                            disabled
                            value={editInput.menuChildType}
                            control={<Radio color="primary" />}
                            label={listMenuChildTypes.map(item => {
                              if (
                                parseInt(editInput.menuChildType, 10) ===
                                item.id
                              ) {
                                return item.name;
                              }
                              return '';
                            })}
                          />
                        </RadioGroup>
                      </TableCell>
                    </TableRow>
                    {(parseInt(editInput.menuChildType, 10) === 3 ||
                      parseInt(editInput.menuChildType, 10) === 4 ||
                      parseInt(editInput.menuChildType, 10) === 5) && (
                      <TableRow>
                        <TableCell className={classes.colLeft}>
                          <RequiredButton />
                        </TableCell>
                        <TableCell className={classes.width100}>
                          <TextField
                            required
                            label="所蔵グループ選択"
                            defaultValue={editInput.groupName}
                            className={classes.textField}
                            margin="normal"
                            InputProps={{
                              readOnly: true
                            }}
                            variant="filled"
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              {parseInt(editInput.menuChildType, 10) !== 3 &&
                parseInt(editInput.menuChildType, 10) !== 4 &&
                parseInt(editInput.menuChildType, 10) !== 5 &&
                isLanguage === languageType.ORIGIN && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <RequiredButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <TextField
                        required
                        label="店舗名"
                        defaultValue={editInput.shopName}
                        className={classes.textField}
                        margin="normal"
                        InputProps={{
                          readOnly: true
                        }}
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
              <TableRow>
                <TableCell className={classes.colLeft}>
                  <RequiredButton />
                </TableCell>
                <TableCell className={classes.colRight}>
                  <TextField
                    required
                    label="メニュー名"
                    defaultValue={editInput.menuName}
                    className={classes.textField}
                    margin="normal"
                    onChange={e => {
                      setMenuName(e.target.value);
                    }}
                    inputProps={{
                      maxLength: isLanguage === languageType.ORIGIN ? 250 : 1000
                    }}
                    onBlur={validMenuName}
                    value={menuName}
                    disabled={
                      isLanguage !== languageType.ORIGIN && translateType === 0
                    }
                  />
                  {(isLanguage === languageType.ORIGIN ||
                    translateType === 1) && (
                    <ValidationText message={errors.menuNameError} />
                  )}
                </TableCell>
                <TableCell className={classes.width100} />
              </TableRow>
              {parseInt(editInput.menuChildType, 10) !== 3 &&
                parseInt(editInput.menuChildType, 10) !== 5 &&
                isLanguage === languageType.ORIGIN && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <RequiredButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <RadioGroup value={editInput.usageType}>
                        <FormControlLabel
                          disabled
                          value={usageType}
                          control={<Radio color="primary" />}
                          label={usageTypes.map(item => {
                            if (usageType === item.id) {
                              return item.name;
                            }
                            return '';
                          })}
                        />
                      </RadioGroup>
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
              {/* {parseInt(editInput.menuChildType, 10) !== 3 &&
                parseInt(editInput.menuChildType, 10) !== 5 &&
                isLanguage === languageType.ORIGIN && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <RequiredButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <Typography className={classes.divisionLabel}>
                        区分
                      </Typography>
                      <RadioGroup value={editInput.menuCombo}>
                        <FormControlLabel
                          disabled
                          value={editInput.menuCombo}
                          control={<Radio color="primary" />}
                          label={editInput.menuCombo === 1 ? '回数券' : '普通'}
                        />
                      </RadioGroup>
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )} */}
              {editInput.menuChildType !== 3 &&
                editInput.menuChildType !== 5 &&
                editInput.menuCombo === 0 &&
                isLanguage === languageType.ORIGIN && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <RequiredButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <Typography className={classes.divisionLabel}>
                        利用方法
                      </Typography>
                      <RadioGroup row value={menuLimitTotal}>
                        <FormControlLabel
                          disabled
                          value={0}
                          control={<Radio color="primary" />}
                          label="一枚単位利用"
                        />
                        <FormControlLabel
                          disabled
                          value={1}
                          control={<Radio color="primary" />}
                          label="一円単位利用"
                        />
                      </RadioGroup>
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
              {enabledStripeBilling === true && editInput.menuChildType !== 5 && (
                <TableRow>
                  <TableCell className={classes.colLeft}>
                    <RequiredButton />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    <Typography className={classes.divisionLabel}>
                      サブスクチケット利用する
                    </Typography>
                    <RadioGroup
                      row
                      value={subscription}
                      onChange={e => {
                        setSubscription(parseInt(e.target.value, 10));
                      }}
                    >
                      <FormControlLabel
                        disabled
                        value={1}
                        control={<Radio color="primary" />}
                        label="利用する"
                      />
                      <FormControlLabel
                        disabled
                        value={0}
                        control={<Radio color="primary" />}
                        label="利用しない"
                      />
                    </RadioGroup>
                  </TableCell>
                  <TableCell className={classes.width100} />
                </TableRow>
              )}
              {subscription === 1 && enabledStripeBilling === true && (
                <TableRow>
                  <TableCell className={classes.colLeft}>
                    <RequiredButton />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    <Typography className={classes.description}>
                      購入頻度設定
                    </Typography>
                    <div className={classes.displayFlex}>
                      <div className={classes.displayBlock}>
                        <TextField
                          id="subscriptionIntervalCount"
                          required
                          label="頻度"
                          className={classes.textFieldPriceCombo}
                          margin="normal"
                          value={subscriptionIntervalCount}
                          onChange={e => {
                            setSubscriptionIntervalCount(e.target.value.trim());
                          }}
                          onBlur={validSubscriptionIntervalCount}
                        />
                        <ValidationText
                          message={errors.subscripIntervalCountError}
                        />
                      </div>
                      <div className={classes.displayBlock}>
                        <TextField
                          id="subscriptionInterval"
                          name="subscriptionInterval"
                          select
                          required
                          label="単位"
                          className={classes.textFieldPriceCombo}
                          margin="normal"
                          value={subscriptionInterval}
                          InputProps={{
                            shrink: true
                          }}
                          onChange={e => {
                            setSubscriptionInterval(e.target.value.trim());
                          }}
                        >
                          <MenuItem key="0" value="WEEK">
                            週間
                          </MenuItem>
                          <MenuItem key="1" value="MONTH">
                            ヶ月
                          </MenuItem>
                        </TextField>
                      </div>
                      <div className={classes.textMutiple}>1回</div>
                    </div>
                  </TableCell>
                  <TableCell className={classes.width100} />
                </TableRow>
              )}
              {editInput.menuCombo === 1 && (
                <>
                  {isLanguage === languageType.ORIGIN && (
                    <TableRow>
                      <TableCell className={classes.colLeft}>
                        <RequiredButton />
                      </TableCell>
                      <TableCell className={classes.colRight}>
                        <Typography className={classes.description}>
                          単価 × 発行枚数
                        </Typography>
                        <div className={classes.displayFlex}>
                          <div className={classes.displayBlock}>
                            <TextField
                              id="priceCombo"
                              required
                              label="単価"
                              className={classes.textFieldPriceCombo}
                              margin="normal"
                              defaultValue={editInput.priceCombo}
                              onChange={e => {
                                setPriceCombo(e.target.value.trim());
                                calcPrice(e);
                              }}
                              onBlur={validPriceCombo}
                            />
                            <ValidationText message={errors.priceComboError} />
                          </div>
                          {/* <div className={classes.priceCalc}>{priceCalc}</div> */}
                          <div className={classes.textMutiple}>円 x</div>
                          <div className={classes.displayBlock}>
                            <TextField
                              id="quantityRelease"
                              name="quantityRelease"
                              select
                              required
                              label="発行枚数"
                              className={classes.textFieldPriceCombo}
                              margin="normal"
                              value={quantity}
                              defaultValue={editInput.quantity}
                              InputProps={{
                                shrink: true
                              }}
                              onChange={e => {
                                setQuantity(e.target.value.trim());
                                calcPrice(e);
                              }}
                              onBlur={validQuantityRelease}
                            >
                              {quantityReleaseList.map(item => {
                                return (
                                  <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                  </MenuItem>
                                );
                              })}
                            </TextField>
                            <ValidationText
                              message={errors.quantityReleaseError}
                            />
                          </div>
                          <div className={classes.textMutiple}>枚</div>
                        </div>
                      </TableCell>
                      <TableCell className={classes.width100} />
                    </TableRow>
                  )}
                  {isLanguage === languageType.ORIGIN && (
                    <TableRow>
                      <TableCell className={classes.colLeft}>
                        <AnyButton />
                      </TableCell>
                      <TableCell className={classes.colRight}>
                        <TextField
                          id="quantityPromotion"
                          name="quantityPromotion"
                          select
                          label="付与無料枚数"
                          className={classes.textField}
                          margin="normal"
                          defaultValue={editInput.quantity}
                          value={quantityFree}
                          InputProps={{
                            shrink: true
                          }}
                          onChange={e => {
                            setQuantityFree(e.target.value);
                          }}
                          onBlur={validQuantityPromotion}
                        >
                          {quantityReleaseList.map(item => {
                            return (
                              <MenuItem key={item.value} value={item.value}>
                                {item.label}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                        <ValidationText
                          message={errors.quantityPromotionError}
                        />
                      </TableCell>
                      <TableCell className={classes.width100} />
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <AnyButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <TextField
                        label="おまけ"
                        multiline
                        rows="4"
                        className={classes.textFieldLong}
                        margin="normal"
                        defaultValue={editInput.quantityFreeDes}
                        onChange={e => {
                          setQuantityFreeDes(e.target.value);
                        }}
                        value={quantityFreeDes}
                        disabled={
                          isLanguage !== languageType.ORIGIN &&
                          translateType === 0
                        }
                        inputProps={{
                          maxLength:
                            isLanguage === languageType.ORIGIN ? 1000 : 2500
                        }}
                      />
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                  {isLanguage === languageType.ORIGIN && (
                    <TableRow>
                      <TableCell className={classes.colLeft}>
                        <AnyButton />
                      </TableCell>
                      <TableCell className={classes.colRight}>
                        <RadioGroup
                          defaultValue={editInput.numberUsePerDay}
                          value={numberUsePerDay}
                          onChange={e => {
                            setNumberUsePerDay(parseInt(e.target.value, 10));
                          }}
                        >
                          <FormControlLabel
                            value={1}
                            control={<Radio color="primary" />}
                            label="1日1枚"
                          />
                          <FormControlLabel
                            value={0}
                            control={<Radio color="primary" />}
                            label="なし"
                          />
                        </RadioGroup>
                      </TableCell>
                      <TableCell className={classes.width100} />
                    </TableRow>
                  )}
                </>
              )}
              {isLanguage === languageType.ORIGIN &&
                parseInt(menuChildType, 10) !== 5 && (
                  <TableRow>
                    <TableCell className={classes.colLeft}>
                      <AnyButton />
                    </TableCell>
                    <TableCell className={classes.colRight}>
                      <Typography className={classes.description}>
                        利用期間
                      </Typography>
                      <RadioGroup
                        row
                        value={expirationType}
                        onChange={e => {
                          setExpirationType(parseInt(e.target.value, 10));
                          resetExpirationValue();
                        }}
                      >
                        <FormControlLabel
                          value={2}
                          control={<Radio color="primary" />}
                          label="指定しない"
                        />
                        <FormControlLabel
                          value={1}
                          control={<Radio color="primary" />}
                          label="期限指定"
                        />
                        <FormControlLabel
                          value={0}
                          control={<Radio color="primary" />}
                          label="期間指定"
                        />
                      </RadioGroup>
                      {expirationType === 0 && (
                        <TextField
                          // id="expiration"
                          // name="expiration"
                          select
                          label="利用期間"
                          className={classes.textField}
                          margin="normal"
                          defaultValue={editInput.expiration}
                          value={expiration}
                          InputProps={{
                            shrink: true
                          }}
                          onChange={e => {
                            setExpiration(e.target.value);
                          }}
                          // variant="filled"
                        >
                          {expirationList.map(item => {
                            return (
                              <MenuItem key={item.value} value={item.value}>
                                {item.label}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      )}
                      {expirationType === 1 && (
                        <TextField
                          label="終了"
                          type="date"
                          className={classes.textField}
                          margin="normal"
                          InputLabelProps={{
                            shrink: true
                          }}
                          defaultValue={editInput.expirationEndDate}
                          value={expirationEndDate}
                          inputProps={{
                            min: minExpirationDate,
                            max: maxExpirationDate
                          }}
                          onChange={e => {
                            setExpirationEndDate(e.target.value);
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell className={classes.width100} />
                  </TableRow>
                )}
              {isLanguage === languageType.ORIGIN && (
                <TableRow>
                  <TableCell className={classes.colLeft}>
                    <RequiredButton />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    <TextField
                      id="price"
                      disabled={editInput.menuCombo === 1}
                      required
                      label="価格"
                      defaultValue={editInput.price}
                      className={classes.textField}
                      margin="normal"
                      value={price}
                      onChange={e => {
                        setPrice(e.target.value.trim());
                        calcPrice(e);
                      }}
                      onBlur={validPrice}
                    />
                    <ValidationText message={errors.priceError} />
                    <div className={classes.priceCalc}>{priceCalc}</div>
                  </TableCell>
                  <TableCell className={classes.width100} />
                </TableRow>
              )}
              {isLanguage === languageType.ORIGIN && (
                <TableRow>
                  <TableCell className={classes.colLeft}>
                    <RequiredButton />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    <div>
                      <TextField
                        id="taxType"
                        name="taxType"
                        select
                        label="税区分・寄付"
                        className={classes.textField}
                        margin="normal"
                        value={taxType}
                        onChange={e => {
                          setTaxType(e.target.value.trim());
                          calcPrice(e);
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                      >
                        {taxTypes.map(group => {
                          return (
                            <MenuItem key={group.id} value={group.id}>
                              {group.name}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          id="reducedTax"
                          checked={reducedTaxCheck}
                        />
                      }
                      label="軽減税率対象"
                      onChange={e => {
                        setReducedTaxCheck(e.target.checked);
                        convertReducedTax(e.target.checked);
                        calcPrice(e);
                      }}
                    />
                  </TableCell>
                  <TableCell className={classes.width100} />
                </TableRow>
              )}
              {isLanguage === languageType.ORIGIN && (
                <TableRow>
                  <TableCell className={classes.colLeft}>
                    <RequiredButton />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    <TextField
                      required
                      select
                      label="ジャンル"
                      className={classes.textField}
                      margin="normal"
                      value={menuGenreId}
                      onChange={e => {
                        setMenuGenreId(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true
                      }}
                      onBlur={validMenuGenre}
                    >
                      {menuGenre &&
                        menuGenre.map(item => {
                          return (
                            <MenuItem
                              key={item.menuGenreId}
                              value={item.menuGenreId}
                            >
                              {item.menuGenreName}
                            </MenuItem>
                          );
                        })}
                    </TextField>
                    <ValidationText message={errors.menuGenreError} />
                  </TableCell>
                  <TableCell className={classes.width100} />
                </TableRow>
              )}
              <TableRow>
                <TableCell className={classes.colLeft}>
                  <AnyButton />
                </TableCell>
                <TableCell className={classes.colRight}>
                  <Typography className={classes.divisionLabel}>
                    金額表示
                  </Typography>
                  <RadioGroup
                    row
                    value={showPrice}
                    defaultValue={editInput.showPrice}
                    onChange={e => {
                      setShowPrice(parseInt(e.target.value, 10));
                    }}
                  >
                    <FormControlLabel
                      disabled={
                        parseInt(menuChildType, 10) === 5 ||
                        (parseInt(menuLimitTotal, 10) === 1 &&
                          parseInt(menuChildType, 10) !== 3 &&
                          menuCombo === 0)
                      }
                      value={1}
                      control={<Radio color="primary" />}
                      label="表示"
                    />
                    <FormControlLabel
                      disabled={
                        parseInt(menuChildType, 10) === 5 ||
                        (parseInt(menuLimitTotal, 10) === 1 &&
                          parseInt(menuChildType, 10) !== 3 &&
                          menuCombo === 0)
                      }
                      value={0}
                      control={<Radio color="primary" />}
                      label="非表示"
                    />
                  </RadioGroup>
                </TableCell>
                <TableCell className={classes.width100} />
              </TableRow>
              {isLanguage === languageType.ORIGIN && (
                <TableRow>
                  <TableCell className={classes.colLeft}>
                    <AnyButton />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    <FormControl className={classes.situationPulldown}>
                      <InputLabel id="situation-label">
                        シチュエーション
                      </InputLabel>
                      <Select
                        labelId="situation-label"
                        multiple
                        value={menuSituationIds}
                        onChange={e => setmenuSituationIds(e.target.value)}
                        input={<Input />}
                        renderValue={() => getSituationName()}
                        className={classes.textFieldLong}
                        MenuProps={{
                          classes: { paper: classes.pulldownHeight },
                          getContentAnchorEl: null
                        }}
                      >
                        {menuSituation &&
                          menuSituation.map(situation => (
                            <MenuItem
                              key={situation.menuSituationId}
                              value={situation.menuSituationId}
                            >
                              <Checkbox
                                checked={
                                  menuSituationIds.indexOf(
                                    situation.menuSituationId
                                  ) > -1
                                }
                              />
                              <ListItemText
                                primary={situation.menuSituationName}
                              />
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell className={classes.width100} />
                </TableRow>
              )}
              {isLanguage === languageType.ORIGIN && (
                <TableRow>
                  <TableCell className={classes.colLeft}>
                    <AnyButton />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    <FormControl className={classes.situationPulldown}>
                      <InputLabel id="user-target-label">贈り相手</InputLabel>
                      <Select
                        labelId="user-target-label"
                        multiple
                        value={menuUserTargetIds}
                        onChange={e => setmenuUserTargetIds(e.target.value)}
                        input={<Input />}
                        renderValue={() => getUserTargetName()}
                        className={classes.textFieldLong}
                        MenuProps={{
                          classes: { paper: classes.pulldownHeight },
                          getContentAnchorEl: null
                        }}
                      >
                        {menuUserTarget &&
                          menuUserTarget.map(target => (
                            <MenuItem
                              key={target.menuUserTargetId}
                              value={target.menuUserTargetId}
                            >
                              <Checkbox
                                checked={
                                  menuUserTargetIds.indexOf(
                                    target.menuUserTargetId
                                  ) > -1
                                }
                              />
                              <ListItemText primary={target.name} />
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell className={classes.width100} />
                </TableRow>
              )}
              <TableRow>
                <TableCell className={classes.colLeft}>
                  <AnyButton />
                </TableCell>
                <TableCell className={classes.colRight}>
                  <TextField
                    label="説明"
                    multiline
                    rows="4"
                    defaultValue={editInput.description}
                    className={classes.textFieldLong}
                    margin="normal"
                    onChange={e => {
                      setDescription(e.target.value);
                    }}
                    value={description}
                    disabled={
                      isLanguage !== languageType.ORIGIN && translateType === 0
                    }
                    inputProps={{
                      maxLength: isLanguage === languageType.ORIGIN ? 250 : 1000
                    }}
                  />
                </TableCell>
                <TableCell className={classes.width100} />
              </TableRow>
              {isLanguage === languageType.ORIGIN && (
                <TableRow>
                  <TableCell className={classes.colLeft}>
                    <AnyButton />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    <TextField
                      label="タグ"
                      defaultValue={editInput.tagStr}
                      className={classes.textFieldLong}
                      margin="normal"
                      value={tagStr}
                      onChange={e => {
                        setTagStr(e.target.value);
                        splitTagStr(e.target.value);
                      }}
                    />
                  </TableCell>
                  <TableCell className={classes.colRight}>
                    #おすすめ #おふくろの味 #学生に ＃ガッツリ
                    などメニューの特徴を表すタグを記載できます。
                  </TableCell>
                </TableRow>
              )}
              {isLanguage === languageType.ORIGIN && (
                <TableRow>
                  <TableCell
                    className={[classes.tableCellBorderNone, classes.colLeft]}
                  >
                    <AnyButton />
                  </TableCell>
                  <TableCell
                    className={[classes.tableCellBorderNone, classes.colRight]}
                  >
                    <FileUpload
                      label="image"
                      file={image}
                      setFile={setImage}
                      previewImage={previewImage}
                      setPreviewImage={setPreviewImage}
                      isOnlyImage
                    />
                  </TableCell>
                  <TableCell className={classes.tableCellBorderNone} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
        <div>
          <CancelButton onClickAction={cancelAction} color="primary" />
          <ConfirmButton onClickAction={confirmAction} />
        </div>
        <Modal open={openEditConfirm}>
          <GochiMenuEditConfirm
            backAction={backAction}
            editAction={editAction}
            editConfirm={multiLangData}
            roles={roles}
            reducedTaxMenuPrefix={reducedTaxMenuPrefix}
            openEditProgress={openEditProgress}
            progressOkAction={progressOkAction}
            loading={loading}
            resultMessage={resultMessage}
            currentLanguage={isLanguage}
            setIsLanguage={setIsLanguage}
            previewImage={previewImage}
            menuGenreName={menuGenreName}
            menuSituationNames={menuSituationNames}
            menuUserTargetNames={menuUserTargetNames}
          />
        </Modal>
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
